<template>
  <div id="app" :class="{ 'has-status-banner': isOffline || pendingCount > 0 }">
    <div v-if="isOffline" class="offline-banner">
      <i class="el-icon-warning"></i>
      You are offline &mdash; clock-ins and clock-outs are saved on this device
      <span v-if="pendingCount > 0"> ({{ pendingCount }} waiting to upload)</span>
    </div>
    <div v-else-if="pendingCount > 0" class="pending-banner">
      <i class="el-icon-loading"></i>
      Uploading {{ pendingCount }} saved item(s)&hellip;
    </div>
    <div v-if="loading" class="spinner-overlay">
      <b-spinner variant="light" label="Loading…" />
    </div>
    <router-view />
    <portal-target name="aiv-portals" multiple />
    <WebClockNudgeModal
      :visible="clockNudge.visible"
      :mode="clockNudge.mode"
      @continue="clockNudge.continueInWeb()"
      @download="clockNudge.dismiss()"
    />
  </div>
</template>

<script>
import { useSpinnerStore } from "src/stores/spinner";
import { useLoginStore } from "src/stores/loginStore";
import { useClockNudgeStore } from "src/stores/clockNudgeStore";
import WebClockNudgeModal from "@/components/WebClockNudgeModal.vue";
import {
  initConnectivityListeners,
  getPending,
  onChange,
} from "@/utils/offlineQueue";
import { shiftsService } from "@/serviceHandlers/shiftsService";

export default {
  name: "App",
  components: { WebClockNudgeModal },
  data() {
    return {
      isOffline: !navigator.onLine,
      pendingCount: getPending().length,
    };
  },
  computed: {
    loading() {
      return useSpinnerStore().active;
    },
    clockNudge() {
      return useClockNudgeStore();
    },
  },
  mounted() {
    // Set up offline queue listeners
    this._cleanupConnectivity = initConnectivityListeners({
      onOnline: () => {
        this.isOffline = false;
      },
      onOffline: () => {
        this.isOffline = true;
      },
      services: {
        "complete-shift-notes": async (payload) => {
          if (payload.isGroupShift && payload.notesData) {
            for (const item of payload.notesData) {
              await shiftsService.completeShiftNotes(item);
            }
          } else if (payload.updateData) {
            await shiftsService.update(
              payload.updateData.shiftId,
              payload.updateData.data,
            );
          }
        },
        // Offline clock events. Payloads carry offlineSync + clientEventId,
        // so the API flags them as offline work and dedupes retries. The
        // queue replays per-shift in order (clock-in before clock-out) and
        // rewrites shift ids when a potential-shift clock-in creates the
        // real row (see utils/offlineQueue.js).
        "clock-in": (payload) => shiftsService.clockIn(payload),
        "clock-out": (payload) => shiftsService.clockOut(payload),
      },
      onQueueProcessed: (result) => {
        if (result.succeeded > 0) {
          this.$notify({
            title: "Synced",
            message: `${result.succeeded} offline item(s) uploaded — your clock times and notes are now on the server.`,
            type: "success",
          });
        }
        if (result.failed > 0) {
          this.$notify({
            title: "Sync needs attention",
            message: `${result.failed} offline item(s) could not be uploaded. Open the sync status chip in the top bar to review.`,
            type: "error",
            duration: 0,
          });
        }
      },
    });

    // Subscribe to queue changes to update pending count
    this._unsubscribeQueue = onChange((pending) => {
      this.pendingCount = pending.length;
    });

    // Dynamically import authService to avoid circular dependencies
    import("@/services/authService").then(({ default: authService }) => {
      const store = useLoginStore();
      const token = localStorage.getItem("jwtToken");

      // Only initialize if user is logged in
      if (token && store.loggedIn) {
        authService.init();

        // Check if token is already expired on app mount
        if (authService.isTokenExpired()) {
          authService.logout("Your session has expired. Please log in again.");
        }
      }

      // Watch for login state changes
      this.$watch(
        () => store.loggedIn,
        (newVal) => {
          if (newVal) {
            authService.init();
          } else {
            authService.destroy();
          }
        }
      );

      // Store authService reference for cleanup
      this.authService = authService;
    });
  },
  beforeDestroy() {
    // Clean up auth service if it was loaded
    if (this.authService) {
      this.authService.destroy();
    }
    if (this._cleanupConnectivity) {
      this._cleanupConnectivity();
    }
    if (this._unsubscribeQueue) {
      this._unsubscribeQueue();
    }
  },
};
</script>

<style>
.spinner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  /* NEVER intercept clicks. The overlay only displays a (non-interactive)
     spinner, so it must let taps fall through to the app underneath. If the
     spinner state ever gets stuck "on", this guarantees the menu and every
     control stay usable instead of the whole UI silently freezing. */
  pointer-events: none;
}

/* While a status banner is shown, push the app below it — the fixed banner
   must never cover the top navbar, which hosts the clock buttons and the
   sync status chip (most important exactly when offline). */
#app.has-status-banner {
  padding-top: 36px;
}

.offline-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: #f56c6c;
  color: #fff;
  text-align: center;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  z-index: 10000;
}

.pending-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: #e6a23c;
  color: #fff;
  text-align: center;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  z-index: 10000;
}
</style>
